import React, { useState, useEffect } from 'react';
import { MessageCircle, QrCode, Link2, Unlink, Check, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { userAPI } from '../api';
import QRCode from 'qrcode';

const TelegramLink = ({ token }) => {
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // { linked: bool, telegram_id: string }
    const [linkData, setLinkData] = useState(null); // { deep_link, qr_data }
    const [qrCodeUrl, setQrCodeUrl] = useState(null);
    const [error, setError] = useState('');

    // Check Telegram link status on mount
    useEffect(() => {
        checkStatus();
    }, [token]);

    const checkStatus = async () => {
        try {
            const result = await userAPI.getTelegramStatus(token);
            setStatus(result);
        } catch (err) {
            console.error('Failed to get Telegram status:', err);
        }
    };

    const generateLink = async () => {
        setLoading(true);
        setError('');
        try {
            const result = await userAPI.generateTelegramLink(token);
            
            if (result.already_linked) {
                setStatus({ linked: true, telegram_id: result.telegram_id });
                return;
            }
            
            setLinkData(result);
            
            // Generate QR code
            const qrDataUrl = await QRCode.toDataURL(result.qr_data, {
                width: 200,
                margin: 2,
                color: {
                    dark: '#00ff88',
                    light: '#0a0a0a'
                }
            });
            setQrCodeUrl(qrDataUrl);
        } catch (err) {
            setError('Gagal generate link. Coba lagi.');
            console.error('Failed to generate Telegram link:', err);
        } finally {
            setLoading(false);
        }
    };

    const unlinkTelegram = async () => {
        setLoading(true);
        try {
            await userAPI.unlinkTelegram(token);
            setStatus({ linked: false });
            setLinkData(null);
            setQrCodeUrl(null);
        } catch (err) {
            setError('Gagal memutus koneksi Telegram.');
        } finally {
            setLoading(false);
        }
    };

    // Already linked
    if (status?.linked) {
        return (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-neon-green/20 rounded-lg">
                        <MessageCircle className="w-6 h-6 text-neon-green" />
                    </div>
                    <div>
                        <h3 className="font-bold text-white">Telegram</h3>
                        <p className="text-sm text-zinc-400">Terhubung</p>
                    </div>
                    <Check className="w-5 h-5 text-neon-green ml-auto" />
                </div>
                
                <div className="bg-zinc-800 rounded-lg p-3 mb-4">
                    <p className="text-sm text-zinc-300">
                        ✅ Akun Telegram Anda sudah terhubung. Anda bisa input score card via bot @vg_wallet_bot
                    </p>
                </div>

                <div className="text-xs text-zinc-500 mb-3">
                    <p>Perintah yang tersedia:</p>
                    <ul className="list-disc list-inside mt-1 space-y-0.5">
                        <li>/skor - Input score card baru</li>
                        <li>/klasemen - Lihat klasemen grup</li>
                        <li>/riwayat - Lihat riwayat score</li>
                        <li>/grup - Lihat daftar grup</li>
                    </ul>
                </div>

                <Button
                    onClick={unlinkTelegram}
                    disabled={loading}
                    variant="outline"
                    size="sm"
                    className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
                >
                    <Unlink className="w-4 h-4 mr-2" />
                    Putuskan Koneksi
                </Button>
            </div>
        );
    }

    // Show QR code and link
    if (linkData && qrCodeUrl) {
        return (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-cyan-500/20 rounded-lg">
                        <QrCode className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                        <h3 className="font-bold text-white">Scan QR Code</h3>
                        <p className="text-sm text-zinc-400">atau klik link di bawah</p>
                    </div>
                </div>

                {/* QR Code */}
                <div className="flex justify-center mb-4">
                    <div className="bg-zinc-800 p-3 rounded-xl">
                        <img src={qrCodeUrl} alt="Telegram QR Code" className="w-48 h-48" />
                    </div>
                </div>

                {/* Instructions */}
                <div className="text-sm text-zinc-400 mb-4 space-y-2">
                    <p className="font-medium text-white">Cara menghubungkan:</p>
                    <ol className="list-decimal list-inside space-y-1">
                        <li>Buka Telegram di HP</li>
                        <li>Scan QR code di atas, atau</li>
                        <li>Klik tombol di bawah</li>
                    </ol>
                </div>

                {/* Deep link button */}
                <a 
                    href={linkData.deep_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-2.5 px-4 bg-[#0088cc] hover:bg-[#0088cc]/90 text-white rounded-lg font-medium transition-colors"
                >
                    <MessageCircle className="w-5 h-5" />
                    Buka di Telegram
                    <ExternalLink className="w-4 h-4" />
                </a>

                {/* Refresh status button */}
                <Button
                    onClick={checkStatus}
                    variant="outline"
                    size="sm"
                    className="w-full mt-3 border-zinc-700"
                >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Cek Status Koneksi
                </Button>
            </div>
        );
    }

    // Initial state - show connect button
    return (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-zinc-800 rounded-lg">
                    <MessageCircle className="w-6 h-6 text-zinc-400" />
                </div>
                <div>
                    <h3 className="font-bold text-white">Telegram Bot</h3>
                    <p className="text-sm text-zinc-400">Belum terhubung</p>
                </div>
            </div>

            <p className="text-sm text-zinc-400 mb-4">
                Hubungkan akun Telegram untuk input score card langsung dari chat!
            </p>

            <div className="text-xs text-zinc-500 mb-4 bg-zinc-800 rounded-lg p-3">
                <p className="font-medium text-zinc-300 mb-2">Fitur Telegram Bot:</p>
                <ul className="space-y-1">
                    <li>📝 Input score card dengan tombol interaktif</li>
                    <li>🏆 Lihat klasemen grup</li>
                    <li>📜 Lihat riwayat score</li>
                    <li>🔔 Notifikasi score baru</li>
                </ul>
            </div>

            {error && (
                <p className="text-sm text-red-400 mb-3">{error}</p>
            )}

            <Button
                onClick={generateLink}
                disabled={loading}
                className="w-full bg-[#0088cc] hover:bg-[#0088cc]/90 text-white"
            >
                {loading ? (
                    <span className="flex items-center gap-2">
                        <span className="spinner w-4 h-4" />
                        Generating...
                    </span>
                ) : (
                    <>
                        <Link2 className="w-4 h-4 mr-2" />
                        Hubungkan Telegram
                    </>
                )}
            </Button>
        </div>
    );
};

export default TelegramLink;
